import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { History, Loader2, User, FileSearch } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";
import { useProject } from "@/context/ProjectContext";
import { useAuth } from "@/context/AuthContext";
import { ORG_BADGE, ORG_LABEL, fmtDateTime } from "@/lib/constants";
import { PmBot } from "@/components/PmBot";

const CAMPI = [
  { v: "percentuale_fornitore", l: "% Stefano Critelli" },
  { v: "percentuale_cliente", l: "% Majorbit" },
  { v: "stato", l: "Stato" },
  { v: "rework_count", l: "Rework" },
  { v: "assegnatario", l: "Assegnatario" },
  { v: "note", l: "Note esecutore" },
  { v: "note_cliente", l: "Note Majorbit" },
];

const Valore = ({ v, tono }) => (
  <span className={`rounded-md border px-2 py-0.5 font-mono text-xs ${tono}`}>
    {v === null || v === undefined || v === "" ? "vuoto" : String(v)}
  </span>
);

export default function Storico() {
  const { user } = useAuth();
  const { project: activeProject, projectId } = useProject();
  const [dati, setDati] = useState({ voci: [], utenti: [] });
  const [accessi, setAccessi] = useState(null);
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState({ utente: "", codice: "", campo: "", dal: "", al: "" });

  const carica = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
      const { data } = await api.get(`/projects/${projectId}/audit`, { params });
      setDati(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [projectId, f]);

  useEffect(() => {
    carica();
  }, [carica]);

  useEffect(() => {
    if (user.role !== "admin") return;
    api.get("/accessi", { params: { limit: 100 } }).then(({ data }) => setAccessi(data)).catch(() => setAccessi(null));
  }, [user.role]);

  const reset = () => setF({ utente: "", codice: "", campo: "", dal: "", al: "" });

  return (
    <div className="space-y-7" data-testid="storico-page">
      <div>
        <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-amber-600">
          {activeProject?.nome}
        </p>
        <h1 className="mt-1 text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl">Storico modifiche</h1>
        <p className="mt-2 max-w-2xl text-sm text-slate-500 md:text-base">
          Chi ha cambiato cosa e quando, per ogni percentuale dichiarata e consolidata.
        </p>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
          <Select value={f.utente || "all"} onValueChange={(v) => setF({ ...f, utente: v === "all" ? "" : v })}>
            <SelectTrigger data-testid="filter-utente" className="lg:col-span-2"><SelectValue placeholder="Tutti gli utenti" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli utenti</SelectItem>
              {dati.utenti.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input
            value={f.codice}
            onChange={(e) => setF({ ...f, codice: e.target.value.toUpperCase() })}
            placeholder="Codice task"
            data-testid="filter-codice"
          />
          <Select value={f.campo || "all"} onValueChange={(v) => setF({ ...f, campo: v === "all" ? "" : v })}>
            <SelectTrigger data-testid="filter-campo"><SelectValue placeholder="Tutti i campi" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i campi</SelectItem>
              {CAMPI.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input type="date" value={f.dal} onChange={(e) => setF({ ...f, dal: e.target.value })} data-testid="filter-dal" />
          <Input type="date" value={f.al} onChange={(e) => setF({ ...f, al: e.target.value })} data-testid="filter-al" />
        </div>
        <div className="mt-3 flex items-center justify-between">
          <p className="font-mono text-xs text-slate-500" data-testid="audit-count">
            {dati.voci.length} modifiche
          </p>
          <Button variant="ghost" size="sm" onClick={reset} data-testid="reset-filters-button">Azzera filtri</Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
      ) : dati.voci.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-slate-300 bg-white py-16 text-center text-sm text-slate-500" data-testid="audit-empty">
          Nessuna modifica registrata con questi filtri.
        </p>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          {dati.voci.map((v) => (
            <div
              key={v.id}
              data-testid={`audit-row-${v.id}`}
              className="flex flex-col gap-2 border-b border-slate-100 p-4 transition-colors last:border-0 hover:bg-slate-50/70 lg:flex-row lg:items-center lg:gap-5"
            >
              <div className="w-44 shrink-0">
                <p className="font-mono text-xs text-slate-500">{fmtDateTime(v.ts)}</p>
                <p className="mt-0.5 flex items-center gap-1.5 text-sm font-semibold text-slate-900">
                  <User className="h-3.5 w-3.5 text-slate-400" /> {v.utente}
                </p>
              </div>

              <div className="min-w-0 flex-1">
                {v.codice ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-md bg-slate-900 px-2 py-0.5 font-mono text-[11px] text-white">{v.codice}</span>
                    {v.org && (
                      <span className={`rounded-md border px-2 py-0.5 text-[10px] font-semibold ${ORG_BADGE[v.org]}`}>
                        {ORG_LABEL[v.org]}
                      </span>
                    )}
                    <span className="truncate text-sm text-slate-700">{v.task}</span>
                  </div>
                ) : (
                  <p className="text-sm font-semibold text-slate-900">{v.azione}</p>
                )}
                {v.campo_label ? (
                  <div className="mt-1.5 flex flex-wrap items-center gap-2">
                    <span className="font-mono text-[11px] uppercase tracking-[0.1em] text-slate-400">{v.campo_label}</span>
                    <Valore v={v.da} tono="border-slate-200 bg-slate-50 text-slate-500 line-through" />
                    <span className="text-slate-300">→</span>
                    <Valore v={v.a} tono="border-emerald-200 bg-emerald-50 text-emerald-700" />
                  </div>
                ) : (
                  v.dettaglio && <p className="mt-1 text-sm text-slate-500">{v.dettaglio}</p>
                )}
              </div>

              <div className="shrink-0">
                <span className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.12em] text-slate-400">
                  <History className="h-3 w-3" /> {v.azione}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {user.role === "admin" && (
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="accessi-card">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="flex items-center gap-2 font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">
              <FileSearch className="h-3.5 w-3.5" /> Accessi ai documenti riservati
            </p>
            <span className="font-mono text-[11px] text-slate-400" data-testid="accessi-count">
              {accessi?.voci?.length || 0} accessi registrati
            </span>
          </div>
          <p className="mt-1.5 text-xs text-slate-500">
            Ogni apertura o download di un documento viene tracciato con utente, ruolo, data, ora e indirizzo IP.
          </p>
          {accessi?.voci?.length ? (
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left">
                    {["Data e ora", "Utente", "Ruolo", "Documento", "IP", "Esito"].map((h) => (
                      <th key={h} className="px-3 py-2 font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {accessi.voci.map((a) => (
                    <tr key={a.id} className="border-b border-slate-100" data-testid={`accesso-${a.id}`}>
                      <td className="whitespace-nowrap px-3 py-2 font-mono text-xs text-slate-500">{fmtDateTime(a.ts)}</td>
                      <td className="px-3 py-2 font-medium text-slate-900">{a.utente}</td>
                      <td className="px-3 py-2 text-slate-500">{a.ruolo}</td>
                      <td className="px-3 py-2 text-slate-700">{a.titolo}</td>
                      <td className="px-3 py-2 font-mono text-xs text-slate-500">{a.ip || "-"}</td>
                      <td className="px-3 py-2">
                        <span className={`rounded-full px-2 py-0.5 font-mono text-[10px] ${a.esito === "negato" ? "bg-rose-50 text-rose-700" : "bg-emerald-50 text-emerald-700"}`}>
                          {a.esito}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="mt-4 text-sm text-slate-400" data-testid="accessi-empty">
              Nessun accesso ai documenti registrato finora.
            </p>
          )}
        </div>
      )}


      <PmBot pagina="storico" />
    </div>
  );
}
